import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {SwissAppConfigModels} from '../models/swissappconfig-models';
import {Capacitor} from '@capacitor/core';
import {Directory, Encoding, Filesystem} from '@capacitor/filesystem';
import {MessageBoxService} from './message-box.service';
import {BenutzerModels} from '../models/benutzer-models';
import {LocalStorageService} from '../local-storage.service';
import * as CryptoJS from 'crypto-js';
import {v4 as uuidv4} from 'uuid';
import {TranslateService} from '@ngx-translate/core';
import {BehaviorSubject, Observable, Subject, Subscription} from 'rxjs';
import {environment} from '../../environments/environment';

@Injectable({
    providedIn: 'root'
})
export class HelperService {

    private configUrl = 'assets/swissappconfig.json';
    private STORAGE_WEB_CONFIG = 'SwissWebConfigDB';
    private webServiceConfig$: BehaviorSubject<SwissAppConfigModels.WebServiceConfig> = new BehaviorSubject({} as any);
    private key = CryptoJS.enc.Utf8.parse('8317958421695473');
    private iv = CryptoJS.enc.Utf8.parse('8317958421695473');
    private onlineStateChanged: Subject<boolean> = new Subject<boolean>();
    private isOnline = true;
    private userSessionToken?: string;
    private checks: { interval: Subscription, function: () => Subscription }[] = [];
    private safetyTimeout?: NodeJS.Timeout;

    constructor(private http: HttpClient, private msgBoxService: MessageBoxService, private localStorageService: LocalStorageService, private translate: TranslateService) {

    }

    public static async toDataUrl(blob: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = () => reject();
            reader.readAsDataURL(blob);
        });
    }

    public static dataURLtoBlob(dataURL: string): Blob {
        const arr = dataURL.split(',');
        // @ts-ignore
        const mime = arr[0].match(/:(.*?);/)[1];
        const bstr = atob(arr[arr.length - 1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        return new Blob([u8arr], {type: mime});
    }

    public static async downLoadFile(blob: Blob, filename: string): Promise<void> {
        const downloadLink = document.createElement('a');
        downloadLink.href = window.URL.createObjectURL(blob);
        if (filename) {
            downloadLink.setAttribute('download', filename);
        }
        document.body.appendChild(downloadLink);
        downloadLink.click();
    }

    async loadSwissWebServiceConfig(): Promise<void> { // wird aus app.module.ts aufgerufen
        return new Promise((resolve, reject) => {
            this.http.get(this.configUrl).subscribe({
                next: (data) => {
                    this.webServiceConfig$?.next(data as SwissAppConfigModels.WebServiceConfig);
                    resolve();
                },
                error: async () => {
                    if (environment.isMobile) {
                        const platform = Capacitor.getPlatform();
                        switch (platform) {
                            case 'ios':
                            case 'android':
                                await this.handleNativePlatformConfig();
                                resolve();
                                break;
                            case 'web':
                            default:
                                this.msgBoxService.showError('couldnt read config', '');
                                reject();
                                return;
                        }
                    } else {
                        this.msgBoxService.showError('couldnt read config', '');
                        reject();
                    }
                }
            });
        });
    }

    async handleNativePlatformConfig(): Promise<void> {
        // Translations arent available yet so the text thats in code is what will be displayed
        let contents: any = {data: undefined};
        try {
            contents = await Filesystem.readFile({
                path: this.configUrl,
                directory: Directory.Data,
                encoding: Encoding.UTF8,
            });
        } catch (ex) {
            const data = await this.msgBoxService.showInputDialog('Web Service Url', 'URL', 'http://webapp.swiss-soft.ch:5002', 'Speichern', 'Abbrechen');
            if (data.doSave) {
                const config = {
                    host: data.value,
                    adrUrl: '/api/Adresse',
                    anwUrl: '/api/Anwender',
                    manUrl: '/api/Mandant',
                    dokuUrl: '/api/Dokument',
                    objUrl: '/api/Objekt',
                    userUrl: '/api/Benutzer',
                    boardUrl: '/api/Dashboard',
                    picUrl: '/api/Bild',
                    mesUrl: '/api/Nachrichten',
                    saUrl: '/api/ServiceApp',
                    termUrl: '/api/Termin',
                    aufgUrl: '/api/Aufgabe',
                    tishUrl: '/api/TimeSheetAPI',
                    noteUrl: '/api/Notiz',
                    weUrl: '/api/Einkauf'
                } satisfies SwissAppConfigModels.WebServiceConfig;
                try {
                    await Filesystem.writeFile({
                        path: this.configUrl,
                        data: JSON.stringify(config),
                        encoding: Encoding.UTF8,
                        directory: Directory.Data,
                        recursive: true
                    });
                } catch (ex) {
                    await this.msgBoxService.askYesNoQuestion('Die Config Datei konnte nicht gespeichert werden', '!!FEHLER!!', [], [], true, 'OK!');
                    await this.handleNativePlatformConfig();
                    return;
                }
                this.webServiceConfig$?.next(config as SwissAppConfigModels.WebServiceConfig);
                return;
            }
            await this.msgBoxService.askYesNoQuestion('Eine Addresse muss eingegeben werden', '!!FEHLER!!', [], [], true, 'OK!');
            await this.handleNativePlatformConfig();
            return;
        }

        if (contents?.data) {
            const config = JSON.parse(contents.data as string) as SwissAppConfigModels.WebServiceConfig;
            if (config.host?.startsWith('https://')) {
                await this.msgBoxService.showErrorOK('Bei der Verwendung unserer Services mit https verwenden Sie bitte die PWA', '!!FEHLER!!', [], [], 'OK');
                await Filesystem.deleteFile({
                    path: this.configUrl,
                    directory: Directory.Data
                });
                await this.handleNativePlatformConfig();
                return;
            }
            this.webServiceConfig$?.next(config);
        }
    }

    public getSwissWebServiceConfig$(): Observable<SwissAppConfigModels.WebServiceConfig> {
        return this.webServiceConfig$.asObservable();
    }

    public getSwissWebServiceConfig(): SwissAppConfigModels.WebServiceConfig {
        return this.webServiceConfig$.getValue();
    }

    public getSwissHttpOptions(user: BenutzerModels.Benutzer, contentType: string = 'application/json'): { headers: HttpHeaders } {
        return {
            headers: new HttpHeaders({
                'Content-Type': contentType,
                Authorization: this.getSwissAuthorization(user)
            }),
        };
    }

    public getSwissAuthorization(user: BenutzerModels.Benutzer): string {
        return 'Basic ' + btoa(user.userName + ':' + user.userPW);
    }

    // Methods for the encrypt and decrypt Using AES
    public encryptUsingAES256(encString: string): string {
        const encrypted = CryptoJS.AES.encrypt(
            CryptoJS.enc.Utf8.parse(JSON.stringify(encString)),
            this.key,
            {
                keySize: 128 / 8,
                iv: this.iv,
                mode: CryptoJS.mode.CBC,
                padding: CryptoJS.pad.Pkcs7,
            }
        );
        return encrypted.toString();
    }

    public decryptUsingAES256(decString: string): string {
        const decrypted = CryptoJS.AES.decrypt(decString, this.key, {
            keySize: 128 / 8,
            iv: this.iv,
            mode: CryptoJS.mode.CBC,
            padding: CryptoJS.pad.Pkcs7,
        });

        return decrypted.toString(CryptoJS.enc.Utf8);
    }

    public getOnlineStateChanged(): Subject<boolean> {
        return this.onlineStateChanged;
    }

    public setOnlineState(isOnline: boolean): void {
        if (this.isOnline !== isOnline) {
            this.isOnline = isOnline;
            this.onlineStateChanged.next(isOnline);
        }
    }

    public isWebServiceAvailable(): boolean {
        return this.isOnline;
    }

    public async loadOrGenSessionGuid(): Promise<string> {
        let userSessionToken = await this.localStorageService.get('user_session_guid', this.STORAGE_WEB_CONFIG, true, undefined);

        if (!userSessionToken) {
            // lost session token (≖_≖)
            userSessionToken = uuidv4();
        }

        this.userSessionToken = userSessionToken;
        await this.localStorageService.add('user_session_guid', this.userSessionToken, this.STORAGE_WEB_CONFIG, false, true);
        return userSessionToken;
    }

    public getUserSessionToken(): string {
        if (!this.userSessionToken) {
            throw Error('usersession token not set');
        }
        return this.userSessionToken;
    }

    public setPageRefreshURL(url: string): Promise<any> {
        return this.localStorageService.add('pageRefreshURL', url, this.STORAGE_WEB_CONFIG, false, true);
    }

    public async delPageRefreshURL(): Promise<void> {
        await this.localStorageService.delete('pageRefreshURL', this.STORAGE_WEB_CONFIG, true);
    }

    public async getPageRefreshURL(): Promise<string> {
        return await this.localStorageService.get('pageRefreshURL', this.STORAGE_WEB_CONFIG, true, '');
    }

    /***
     * get locales from server instead of service worker
     ***/
    public async updateTranslations(): Promise<boolean> {
        const h = new HttpHeaders().set('ngsw-bypass', '1');
        return new Promise(resolve => {
            this.http.get('/assets/i18n/de.json?cache-bust=true&ngsw-bypass', {headers: h}).subscribe({
                next: (res) => {
                    this.translate.setTranslation('de', res);
                    resolve(true);
                },
                error: (err) => {
                    console.error('could not load translations: de');
                    console.log(err);
                    resolve(false);
                }
            });
            this.http.get('/assets/i18n/fr.json?cache-bust=true&ngsw-bypass', {headers: h}).subscribe({
                next: (res) => {
                    this.translate.setTranslation('fr', res);
                },
                error: (err) => {
                    console.error('could not load translations: fr');
                    console.log(err);
                }
            });
        });
    }

    public pausePeriodicalChecks(): void {
        this.checks.forEach(entry => {
            entry.interval.unsubscribe();
            entry.interval.closed = true;
        });
    }

    public restartPeriodicalChecks(): void {
        const checks: any[] = ([] as any).concat(this.checks);
        this.checks = [];
        checks.forEach(entry => {
            entry.interval.unsubscribe();
            entry.interval.closed = true;
            this.addPeriodicalCheck(entry.function);
        });
    }

    public addPeriodicalCheck(task: () => Subscription): void {
        this.checks.push({interval: task(), function: task});
    }

    public lockApp(noTimeOut = false, allowOverlays = false): void {
        if (document.getElementsByTagName('body')[0].querySelectorAll('.swiss-soft-update-blocker').length > 0) {
            return;
        }
        const blocker = document.createElement('div');
        blocker.classList.add('swiss-soft-update-blocker');
        if (allowOverlays) {
            blocker.classList.add('allow-overlays');
            this.msgBoxService.blockUserInputs();
        }

        const spinner = document.createElement('div');
        spinner.classList.add('swiss-soft-spinner');
        blocker.appendChild(spinner);

        document.getElementsByTagName('body')[0].appendChild(blocker);

        if (noTimeOut) {
            return;
        }
        this.safetyTimeout = setTimeout(() => {
            document.location.reload();
        }, 300000);
    }

    public unlockApp(): void {
        clearTimeout(this.safetyTimeout);
        this.msgBoxService.allowUserInputDialogs();

        const blocker = document.querySelectorAll('.swiss-soft-update-blocker');
        if (blocker.length !== 1) {
            return;
        }
        document.getElementsByTagName('body')[0].removeChild(blocker[0]);
    }
}
